import React from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, Clock, ArrowRight } from 'lucide-react';

const STATS = [
  { delay: '7-day delay', lost: '$345', sub: 'at $1,500/mo rent' },
  { delay: '14-day delay', lost: '$690', sub: 'at $1,500/mo rent' },
  { delay: '30-day delay', lost: '$1,500', sub: 'a full month of rent' },
];

export default function RealCost() {
  return (
    <section
      className="section-pad bg-ohana-surface border-y border-ohana-border"
      data-testid="real-cost-section"
    >
      <div className="container-ohana">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-5">
            <p className="overline !text-ohana-accent-dark">For Landlords &amp; Property Managers</p>
            <h2
              className="h-display text-4xl md:text-5xl mt-3 leading-tight"
              data-testid="real-cost-headline"
            >
              The Real Cost.
            </h2>
            <p className="mt-5 text-ohana-text-muted text-base md:text-lg leading-relaxed">
              Every day a unit sits full of someone else&apos;s stuff, you&apos;re losing money. The longer the cleanout drags, the longer the unit stays off market.
            </p>
            <p className="mt-4 text-ohana-text leading-relaxed">
              Most cleanout companies take a week to schedule, then another week to finish. Two weeks of lost rent on a single unit is more than the cost of the cleanout itself.
            </p>

            <div className="mt-8 flex items-start gap-3 rounded-xl border-l-4 border-ohana-accent bg-white px-5 py-4">
              <Clock className="h-5 w-5 flex-none text-ohana-accent mt-0.5" />
              <p className="text-sm text-ohana-text leading-relaxed">
                <span className="font-semibold text-ohana-primary">Ohana turnaround: 24-48 hours.</span> Photo-based quote, scheduled fast, finished in one visit so your unit is rent-ready before listing photos go live.
              </p>
            </div>

            <div className="mt-7">
              <Link
                to="/contact"
                className="btn-primary"
                data-testid="real-cost-cta"
              >
                Get a Free Quote for Your Unit
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="rounded-2xl bg-white border border-ohana-border p-6 md:p-8">
              <div className="flex items-center gap-3">
                <span className="grid h-10 w-10 place-items-center rounded-lg bg-red-50 text-red-700">
                  <AlertTriangle className="h-5 w-5" />
                </span>
                <p className="font-serif text-xl text-ohana-primary leading-tight">
                  The math on a delayed cleanout
                </p>
              </div>

              <ul
                className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4"
                data-testid="real-cost-math"
              >
                {STATS.map((s) => (
                  <li
                    key={s.delay}
                    className="rounded-xl bg-ohana-bg border border-ohana-border p-5"
                  >
                    <p className="text-xs uppercase tracking-wider text-ohana-text-muted font-semibold">
                      {s.delay}
                    </p>
                    <p className="mt-2 font-serif text-3xl text-red-700">{s.lost}</p>
                    <p className="mt-1 text-xs text-ohana-text-muted leading-snug">{s.sub}</p>
                  </li>
                ))}
              </ul>

              <p className="mt-6 text-sm text-ohana-text-muted leading-relaxed">
                Numbers assume a single unit at $1,500/month. Scale up across a portfolio and a few weeks of delay can cost more than the cleanout, the listing photos, and the cleaning fee combined.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
